import { useState } from "react";
import { getFileByModule } from "../../services/FilesService/FilesService";
import { useFilePreview } from "../../hooks/useFilePreview";
import FilePreviewModal from "../../components/FilePreviewModal/FilePreviewModal";
import { downloadFile } from "../../utils/fileUtils";
import toast from "react-hot-toast";

const groups = [
  { key: "Кіші топ", age: "2-3", code: "kishi_top" },
  { key: "Ортаңғы топ", age: "3-4", code: "ortangy_top" },
  { key: "Ересек топ", age: "4-5", code: "eresek_top" },
  { key: "Мектепалды топ", age: "5-6", code: "mektep_aldy_top" },
];

const months = [
  { number: 0, label: "Қаңтар", short: "Қаң" },
  { number: 1, label: "Ақпан", short: "Ақп" },
  { number: 2, label: "Наурыз", short: "Нау" },
  { number: 3, label: "Сәуір", short: "Сәу" },
  { number: 4, label: "Мамыр", short: "Мам" },
  { number: 5, label: "Маусым", short: "Мау" },
  { number: 6, label: "Шілде", short: "Шіл" },
  { number: 7, label: "Тамыз", short: "Там" },
  { number: 8, label: "Қыркүйек", short: "Қыр" },
  { number: 9, label: "Қазан", short: "Қаз" },
  { number: 10, label: "Қараша", short: "Қар" },
  { number: 11, label: "Желтоқсан", short: "Жел" },
];

const MonthlyCyclogram = () => {
  const [selectedGroup, setSelectedGroup] = useState(groups[3]); // Default to last group
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [isDownloading, setIsDownloading] = useState(false);

  // File preview hook
  const {
    isPreviewOpen,
    previewUrl,
    isLoading: isPreviewLoading,
    openPreview,
    closePreview,
  } = useFilePreview();

  const fetchFileMeta = async () => {
    try {
      const response = await getFileByModule({
        module_code: "monthly_cyclogram",
        month: selectedMonth + 1,
        group: selectedGroup.code,
      });

      if (!response?.file) {
        toast.error("Файл табылмады.");
        return null;
      }
      return response.file;
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Файл табылмады немесе қате болды.");
      return null;
    }
  };

  const handlePreview = async () => {
    const file = await fetchFileMeta();
    if (!file) return;
    await openPreview(file.file_url, file.file_name);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const file = await fetchFileMeta();
      if (!file) return;
      downloadFile(file.file_url, file.file_name);
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Файл жүктеу кезінде қате болды.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-light text-[#674DA2] mb-2">
            Айлық Циклограмма
          </h1>
          <p className="text-gray-500 text-sm">Топ пен айды таңдаңыз</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Month Selection */}
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-sm font-medium text-[#674DA2] mb-4">Ай</h3>
            <div className="grid grid-cols-6 gap-2">
              {months.map((month) => (
                <button
                  key={month.number}
                  onClick={() => setSelectedMonth(month.number)}
                  className={`py-2 px-3 text-sm rounded-md transition-colors ${
                    selectedMonth === month.number
                      ? "bg-[#674DA2] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-[#674DA2]/10 hover:text-[#674DA2]"
                  }`}
                >
                  {month.label}
                </button>
              ))}
            </div>
          </div>

          {/* Group Selection */}
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-sm font-medium text-[#674DA2] mb-4">Топ</h3>
            <div className="grid grid-cols-2 gap-3">
              {groups.map((group) => (
                <button
                  key={group.code}
                  onClick={() => setSelectedGroup(group)}
                  className={`p-4 text-left rounded-md border transition-colors ${
                    selectedGroup.code === group.code
                      ? "border-[#674DA2] bg-[#674DA2]/10 text-[#674DA2]"
                      : "border-gray-200 bg-white text-gray-700 hover:border-[#674DA2] hover:bg-[#674DA2]/5"
                  }`}
                >
                  <div className="font-medium text-sm">{group.key}</div>
                  <div
                    className={`text-xs ${
                      selectedGroup.code === group.code
                        ? "text-[#674DA2]/70"
                        : "text-gray-500"
                    }`}
                  >
                    {group.age} жас
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Selected Info & Actions */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h4 className="font-medium text-[#674DA2]">Таңдалған:</h4>
                <p className="text-sm text-gray-600">
                  {selectedGroup.key} • {months[selectedMonth].label}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handlePreview}
                disabled={isPreviewLoading}
                className="flex-1 bg-[#674DA2] hover:bg-[#5b4196] disabled:bg-gray-400 text-white py-3 px-4 rounded-md text-sm font-medium transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isPreviewLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Жүктелуде...</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    <span>Көру</span>
                  </>
                )}
              </button>

              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="flex-1 bg-[#F3DA6D] hover:bg-[#ebd059] disabled:bg-gray-400 disabled:text-gray-600 text-black py-3 px-4 rounded-md text-sm font-medium transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDownloading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    <span>Жүктелуде...</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span>Жүктеп алу</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        previewUrl={previewUrl}
        onClose={closePreview}
        title={`Айлық Циклограмма - ${selectedGroup.key} - ${months[selectedMonth].label}`}
      />
    </div>
  );
};

export default MonthlyCyclogram;
